/**
 * The flame's frames, as pixels.
 *
 * **T108 slice 6 · `Development/Minecraft/15_Animated_Textures_And_The_Flame.md`**
 *
 * 🎯 **haugur chose painted frames over a flame written in the shader**, and
 * this is that: an ordinary strip of pictures, generated once by
 * `tools/textures/flame.mjs` and committed, loaded like any other texture.
 * **Nothing here is computed at runtime.**
 *
 * 🔴 **The loop is exact by construction.** Frame 32 leads back into frame 1
 * with no jump, because the noise field the generator uses is periodic and
 * scrolls a whole number of periods across the frames. **Four photographed
 * flames could never do that** — doc 15 §6.1.
 *
 * ⚠️ **Raw RGBA rather than PNG**, for the same reason as `block-tiles.json`:
 * this package is zero-dependency and is imported by Node tools as well as by
 * the browser. `atob` exists in both; a PNG decoder would have to be written
 * twice.
 */

import flameData from '../data/flame-frames.json' with { type: 'json' };

/** Texels along one edge of a frame. */
export const FLAME_SIZE: number = flameData.size;

/** How many frames the loop has. */
export const FLAME_FRAMES: number = flameData.frames;

/**
 * Which candidate haugur picked, and what it was called when he picked it.
 *
 * ⚠️ **Kept because a picture in a data file cannot say why it is that
 * picture.** Regenerating with different parameters silently replaces the
 * flame, and this is the only record of what was chosen.
 */
export const FLAME_CHOSEN: string = flameData.$chosen;

/**
 * Every frame, one after another — `FLAME_FRAMES` blocks of
 * `FLAME_SIZE × FLAME_SIZE × 4` bytes, in order.
 *
 * 👉 **Laid out exactly as a `DataArrayTexture` wants it**, so the client
 * uploads it in one call with a layer per frame — the same machinery the block
 * tiles already use, rather than a second way of doing textures.
 */
/* ⚠️ No explicit `Uint8Array` annotation. The bare type is
   `Uint8Array<ArrayBufferLike>`, which three.js refuses because a
   SharedArrayBuffer cannot back a texture upload — letting it infer keeps the
   narrower `Uint8Array<ArrayBuffer>` that `new Uint8Array(n)` really produces. */
export const FLAME_RGBA = (() => {
  const bin = atob(flameData.rgba);
  const bytes = new Uint8Array(bin.length);
  /* eslint-disable-next-line security/detect-object-injection --
     A loop counter writing into a Uint8Array this function just allocated. A
     typed array cannot suffer prototype pollution; same reasoning as the
     identical decode in textures.ts. */
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
})();

/**
 * ⏱️ **How long one frame is held, in real milliseconds.**
 *
 * **Minecraft's campfire holds each frame for 2 ticks — 100 ms — which is half
 * the speed of open fire**, and that is the number copied here. ⚠️ **A campfire
 * is a calmer thing than a burning building**, and the only dial Minecraft
 * turns between the two is the speed.
 *
 * 🔴 **Real milliseconds, not game time.** A fire flickers at the speed a fire
 * flickers; it does not run 72× faster because the world clock does. **The
 * only thing on the game clock is how long the log lasts.**
 */
export const FLAME_FRAME_MS = 100;

/**
 * How far into the loop the second sheet is, in frames.
 *
 * 🔴 **This is the cheapest good idea in doc 15.** Minecraft draws its fire
 * twice and gives one copy a frame list starting halfway through, so the two
 * sheets never agree and the flame stops reading as a repeating loop.
 * **One number, no extra art.**
 */
export const FLAME_SECOND_SHEET_OFFSET = Math.floor(FLAME_FRAMES / 2);

/** Which frame is showing at a given moment. Wraps, so it never runs out. */
export function flameFrameAt(elapsedMs: number, offset = 0): number {
  const step = Math.floor(elapsedMs / FLAME_FRAME_MS) + offset;
  return ((step % FLAME_FRAMES) + FLAME_FRAMES) % FLAME_FRAMES;
}
